<template>
  <AuthPage v-if="newUiEnabled" :wide="currentStep !== 'slug_input'">
    <UiLoading v-if="currentStep === 'loading'" />

    <template v-else-if="currentStep === 'error'">
      <AuthCardHeader :icon="UserPlus" :title="$t('Registration')" />

      <UiAlert tone="danger">
        {{ $t("Registration is temporarily unavailable. Please try again later.") }}
      </UiAlert>

      <div class="auth-actions">
        <UiButton
          variant="secondary"
          :icon="RotateCw"
          class="auth-actions__back"
          @click="router.go(0)"
        >
          {{ $t("Try again") }}
        </UiButton>

        <UiButton variant="primary" :icon="LogIn" @click="router.push({ name: 'auth.login' })">
          {{ $t("Log in") }}
        </UiButton>
      </div>
    </template>

    <InstitutionSlugInput
      v-else-if="currentStep === 'slug_input'"
      ref="slugInputRef"
      @submit="onSlugSubmit"
    />

    <template v-else>
      <AuthCardHeader
        v-if="!registrationCompleted"
        :icon="UserPlus"
        :title="$t('Registration')"
        :subtitle="registrationTypeLabel"
      >
        <UiButton
          v-if="currentStep === 'form' && canGoBackToModeSelection"
          variant="secondary"
          :icon="ChevronLeft"
          class="auth-actions__back"
          @click="goBackToModeSelection"
        >
          {{ $t("Change registration type") }}
        </UiButton>

        <UiButton
          v-else-if="currentStep === 'form' && canGoBackToSlugInput"
          variant="secondary"
          :icon="ChevronLeft"
          class="auth-actions__back"
          @click="goBackToSlugInput"
        >
          {{ $t("Change institution") }}
        </UiButton>
      </AuthCardHeader>

      <RegisterModeSelector
        v-if="currentStep === 'mode_selection'"
        :modes="modesData?.modes || []"
        @select="onModeSelected"
      />

      <RegisterForm
        v-else
        :submit="onSubmit"
        :allergens-options="allergensOptions"
        :classrooms-options="classroomsOptions"
        :institution-workers-groups-options="institutionWorkersGroupsOptions"
        :catering-terms-of-use="cateringTermsOfUse"
        :register-as="registerAs"
        :invoices-config-for-users="invoicesConfigForUsers"
        :locked-institution="lockedInstitution"
        @create-child="onCreateChildButtonClick"
      >
        <FormSection
          v-if="registerAs === 'parent' && !hideChildrenTable"
          :title="$t('Meal receivers list')"
          icon="fa-users"
        >
          <template v-if="childrenData.length > 0">
            <UiDataTable :columns="childrenColumns" :items="childrenRows" row-key="index">
              <template #cell-number="{ index }">{{ index + 1 }}</template>

              <template #cell-actions="{ item }">
                <UiRowActions>
                  <b-button
                    variant="secondary"
                    @click.prevent.stop="onEditChildDataButtonClick(item.childData, item.index)"
                  >
                    <UiIcon fa="fa fa-edit" />
                    {{ $t("Edit") }}
                  </b-button>

                  <b-button
                    variant="danger"
                    @click.prevent.stop="onDeleteChildDataButtonClick(item.index)"
                  >
                    <UiIcon fa="fa fa-trash" />
                    {{ $t("Remove") }}
                  </b-button>
                </UiRowActions>
              </template>

              <template #card="{ item }">
                <UiDataField :label="$t('Firstname and Lastname')">{{ item.name }}</UiDataField>

                <div class="auth-register-children__card-actions">
                  <UiButton
                    variant="secondary"
                    :icon="Pencil"
                    @click="onEditChildDataButtonClick(item.childData, item.index)"
                  >
                    {{ $t("Edit") }}
                  </UiButton>

                  <UiButton
                    variant="danger"
                    :icon="Trash2"
                    @click="onDeleteChildDataButtonClick(item.index)"
                  >
                    {{ $t("Remove") }}
                  </UiButton>
                </div>
              </template>
            </UiDataTable>

            <UiButton
              variant="secondary"
              :icon="Plus"
              class="auth-register-children__add"
              @click="onCreateChildButtonClick"
            >
              {{ $t("Add next child") }}
            </UiButton>
          </template>

          <UiAlert v-else action-placement="right">
            {{ $t("Meal receivers list is empty.") }}

            <template #action>
              <UiButton variant="secondary" :icon="Plus" @click="onCreateChildButtonClick">
                {{ $t("Add child") }}
              </UiButton>
            </template>
          </UiAlert>
        </FormSection>
      </RegisterForm>
    </template>
  </AuthPage>

  <div v-else class="app flex-row align-items-center">
    <div class="container">
      <!-- STEP: Loading -->
      <div
        v-if="currentStep === 'loading'"
        class="d-flex justify-content-center align-items-center py-5"
        style="min-height: 300px"
      >
        <Loading />
      </div>

      <!-- STEP: Error -->
      <div
        v-else-if="currentStep === 'error'"
        class="d-flex flex-column justify-content-center align-items-center py-5 gap-3"
        style="min-height: 300px"
      >
        <b-alert :model-value="true" variant="danger" class="mb-0">
          {{ $t("Registration is temporarily unavailable. Please try again later.") }}
        </b-alert>

        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="router.go(0)">
            <UiIcon fa="fa fa-refresh me-1" />
            {{ $t("Try again") }}
          </button>

          <router-link :to="{ name: 'auth.login' }" class="btn btn-primary btn-sm">
            {{ $t("Log in") }}
          </router-link>
        </div>
      </div>

      <!-- STEP: Number Input (hidden institutions mode) -->
      <InstitutionSlugInput
        v-else-if="currentStep === 'slug_input'"
        ref="slugInputRef"
        @submit="onSlugSubmit"
      />

      <!-- STEP: Mode Selection or Form -->
      <b-row v-else class="justify-content-center animated fadeIn">
        <b-col sm="12" md="10" lg="8" class="text-center">
          <img :src="logo" :alt="logoAlt" class="login-form-image mb-4" />

          <h1 class="mb-4">{{ $t("Registration") }}</h1>

          <b-card no-body class="register-card p-3 p-sm-4">
            <!-- Back button + registration type info (hidden after successful registration) -->
            <template v-if="currentStep === 'form' && !registrationCompleted">
              <div class="mb-1 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <button
                    v-if="canGoBackToModeSelection"
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="goBackToModeSelection"
                  >
                    <UiIcon fa="fa fa-arrow-left me-1" />
                    {{ $t("Change registration type") }}
                  </button>
                  <button
                    v-else-if="canGoBackToSlugInput"
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="goBackToSlugInput"
                  >
                    <UiIcon fa="fa fa-arrow-left me-1" />
                    {{ $t("Change institution") }}
                  </button>
                </div>

                <div class="registration-type-badge">
                  <UiIcon
                    :fa="'me-1 ' + (registerAs === 'parent' ? 'fa fa-users' : 'fa fa-user')"
                  />
                  {{
                    $t(
                      registerAs === "parent"
                        ? "register_as_parent"
                        : "register_as_institution_worker",
                    )
                  }}
                </div>
              </div>

              <hr />
            </template>

            <!-- Mode Selection -->
            <RegisterModeSelector
              v-if="currentStep === 'mode_selection'"
              :modes="modesData?.modes || []"
              @select="onModeSelected"
            />

            <!-- Registration Form -->
            <template v-else>
              <RegisterForm
                :submit="onSubmit"
                :allergens-options="allergensOptions"
                :classrooms-options="classroomsOptions"
                :institution-workers-groups-options="institutionWorkersGroupsOptions"
                :catering-terms-of-use="cateringTermsOfUse"
                :register-as="registerAs"
                :invoices-config-for-users="invoicesConfigForUsers"
                :locked-institution="lockedInstitution"
                @create-child="onCreateChildButtonClick"
              >
                <!-- Children section (only for parent mode) -->
                <FormSection
                  v-if="registerAs === 'parent' && !hideChildrenTable"
                  :title="$t('Meal receivers list')"
                  icon="fa-users"
                >
                  <!-- Children list -->
                  <template v-if="childrenData.length > 0">
                    <div class="table-responsive mb-3">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>L.P.</th>
                            <th>{{ $t("Firstname and Lastname") }}</th>
                            <th>{{ $t("Actions") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(childData, index) in childrenData" :key="index">
                            <td class="align-middle">{{ index + 1 }}</td>
                            <td class="align-middle">
                              {{ childData.firstName }} {{ childData.lastName }}
                            </td>
                            <td class="align-middle">
                              <button
                                type="button"
                                class="btn btn-outline-secondary btn-sm me-1"
                                @click.prevent.stop="onEditChildDataButtonClick(childData, index)"
                              >
                                <UiIcon fa="fa fa-edit" />
                              </button>
                              <button
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                @click.prevent.stop="onDeleteChildDataButtonClick(index)"
                              >
                                <UiIcon fa="fa fa-trash" />
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      @click.prevent.stop="onCreateChildButtonClick"
                    >
                      <UiIcon fa="fa fa-plus me-1" />
                      {{ $t("Add next child") }}
                    </button>
                  </template>

                  <!-- Empty state -->
                  <b-alert v-else :model-value="true" variant="warning" class="mb-0">
                    <div
                      class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2"
                    >
                      <span>{{ $t("Meal receivers list is empty.") }}</span>
                      <button
                        type="button"
                        class="btn btn-outline-primary btn-sm"
                        @click.prevent.stop="onCreateChildButtonClick"
                      >
                        <UiIcon fa="fa fa-plus me-1" />
                        {{ $t("Add child") }}
                      </button>
                    </div>
                  </b-alert>
                </FormSection>
              </RegisterForm>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>

  <!-- CreateChildModal -->
  <CreateChildModal
    v-if="registerAs === 'parent'"
    variant="register"
    :is-visible="createChildModalVisible"
    :selected-child-data="selectedChildData"
    :default-institution-id="lockedInstitution?.id"
    :default-institution-name="lockedInstitution?.name"
    :institution-slug="resolvedInstitutionSlug"
    @child-data-created="onChildDataCreated"
    @child-data-edited="onChildDataEdited"
    @hide="hideCreateChildModal"
  />

  <!-- ExistingAccountPasswordModal -->
  <ExistingAccountPasswordModal
    :is-visible="existingAccountModalVisible"
    :email-address="existingAccountEmail"
    :is-submitting="existingAccountModalSubmitting"
    :error-message="existingAccountModalError"
    @submit="onExistingAccountPasswordSubmit"
    @hide="onExistingAccountModalHide"
  />
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import RegisterForm from "../components/RegisterForm.vue";
import RegisterModeSelector from "../components/RegisterModeSelector.vue";
import InstitutionSlugInput from "../components/InstitutionSlugInput.vue";
import ExistingAccountPasswordModal from "../components/ExistingAccountPasswordModal.vue";
import CreateChildModal from "@/modules/children/components/CreateChildModal.vue";
import Loading from "@/components/Loading.vue";
import FormSection from "@/components/Form/FormSection.vue";
import AuthPage from "../components/newUi/AuthPage.vue";
import AuthCardHeader from "../components/newUi/AuthCardHeader.vue";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";
import { ChevronLeft, LogIn, Pencil, Plus, RotateCw, Trash2, UserPlus } from "lucide-vue-next";

import { useRegisterModesQuery, type RegistrationMode } from "../queries/useRegisterModesQuery";
import { usePublicTenantSettings } from "@/modules/settings/queries/usePublicTenantSettings";
import { showApiErrorNotification } from "@/lib/axios-interceptor";

// an e-mail that already has an account ("global user") on another tenant must not be silently
// attached to this one - the backend requires proof of ownership (the existing account's
// password) before linking, see obiado-backend GlobalUserAlreadyExistsForEmailException
const EMAIL_ALREADY_REGISTERED_ELSEWHERE_ERROR_KEY =
  "exceptions.auth.email_already_registered_elsewhere";

// too many wrong-password attempts against the existing account - keep the modal open
const EXISTING_ACCOUNT_LINK_ATTEMPTS_RATE_LIMITED_ERROR_KEY =
  "exceptions.auth.existing_account_link_attempts_rate_limited";

// Types
interface ChildData {
  firstName: string;
  lastName: string;
  [key: string]: unknown;
}

interface SelectOption {
  value: number | string;
  text: string;
  [key: string]: unknown;
}

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const newUiEnabled = useNewUiEnabled();

// Logo
const logo = computed(() => "/img/obiado.svg");
const logoAlt = computed(() => "Obiado logo");

// Refs for child components
const slugInputRef = ref<InstanceType<typeof InstitutionSlugInput> | null>(null);

// Route params
const routeInstitutionSlug = computed(() => route.params.institutionSlug as string | undefined);

// Manual number input (for hidden institutions mode)
const manualSlug = ref<string | undefined>(undefined);

// Manual entry wins once set - the only way to recover from a route slug that failed to resolve.
const effectiveSlug = computed(() => manualSlug.value ?? routeInstitutionSlug.value);

// Queries
const {
  data: publicSettings,
  isPending: isLoadingPublicSettings,
  isError: isPublicSettingsError,
} = usePublicTenantSettings({ forceEnabled: true });

// Reactive derivations from public tenant settings
const hideInstitutions = computed(
  () => !!publicSettings.value?.parents?.hideInstitutionsListsFromParents,
);
const hideInstitutionsLoaded = computed(
  () => !isLoadingPublicSettings.value && publicSettings.value != null,
);

// Only fetch modes when we have a slug (or hideInstitutions is false)
const shouldFetchModes = computed(() => {
  // Wait for hideInstitutions setting to load first
  if (!hideInstitutionsLoaded.value) {
    return false;
  }

  // If we have a slug from URL, always fetch
  if (routeInstitutionSlug.value) {
    return true;
  }

  // If institutions are hidden and no manual slug yet, don't fetch
  if (hideInstitutions.value && !manualSlug.value) {
    return false;
  }

  // Fetch: either normal mode, or hidden mode with manual slug provided
  return true;
});

const {
  data: modesData,
  isPending: isLoadingModes,
  error: modesError,
} = useRegisterModesQuery(effectiveSlug, { enabled: shouldFetchModes });

// /register/modes falls back silently (no institutionId) instead of erroring on a bad slug - treat that the same as modesError.
const slugFailedToResolve = computed(
  () =>
    !!effectiveSlug.value &&
    (!!modesError.value ||
      (!isLoadingModes.value && modesData.value != null && !modesData.value.institutionId)),
);

// State
const selectedMode = ref<RegistrationMode | null>(null);
const childrenData = ref<ChildData[]>([]);
const createChildModalVisible = ref(false);
const selectedChildData = ref<ChildData | null>(null);
const selectedChildDataIndex = ref<number | null>(null);
const hideChildrenTable = ref(false);
const registrationCompleted = ref(false);
const existingAccountModalVisible = ref(false);
const existingAccountEmail = ref<string | null>(null);
const existingAccountModalSubmitting = ref(false);
const existingAccountModalError = ref<string | null>(null);

// Holds the promise callbacks for a registration attempt parked while the existing-account
// password modal is open - not reactive state, just plumbing to resolve/reject onSubmit's
// promise once the modal is resolved.
let pendingRegistration: {
  registerData: Record<string, unknown>;
  userType: string;
  resolve: (result: unknown) => void;
  reject: (error: unknown) => void;
} | null = null;
const cateringTermsOfUse = ref<string | undefined>(undefined);
const invoicesConfigForUsers = computed(
  () =>
    publicSettings.value?.accounting ?? {
      enableInvoicesForUsers: false,
      requireInvoiceConfigForUsers: false,
    },
);

// Options for RegisterForm
const allergensOptions = ref<SelectOption[]>([]);
const classroomsOptions = ref<SelectOption[]>([]);
const institutionWorkersGroupsOptions = ref<SelectOption[]>([]);

// Current step in the flow
type RegistrationStep = "loading" | "error" | "slug_input" | "mode_selection" | "form";

const currentStep = computed<RegistrationStep>(() => {
  // Public settings failed to load — show error instead of infinite spinner
  if (isPublicSettingsError.value) {
    return "error";
  }

  // Still loading initial data
  if (isLoadingPublicSettings.value || !hideInstitutionsLoaded.value) {
    return "loading";
  }

  if (hideInstitutions.value && (!effectiveSlug.value || slugFailedToResolve.value)) {
    return "slug_input";
  }

  // Loading modes
  if (isLoadingModes.value) {
    return "loading";
  }

  // Show mode selection if needed
  if (modesData.value?.showModeSelection && !selectedMode.value) {
    return "mode_selection";
  }

  // Show form
  return "form";
});

const registerAs = computed(() => selectedMode.value || modesData.value?.modes[0] || "parent");

const registrationTypeLabel = computed(() => {
  if (currentStep.value === "mode_selection") {
    return t("Choose how you want to register");
  }

  if (currentStep.value !== "form") {
    return undefined;
  }

  return t(registerAs.value === "parent" ? "register_as_parent" : "register_as_institution_worker");
});

const childrenColumns = computed(() => [
  { key: "number", label: "L.P." },
  { key: "name", label: t("Firstname and Lastname") },
  { key: "actions", label: t("Actions") },
]);

const childrenRows = computed(() =>
  childrenData.value.map((childData, index) => ({
    index,
    childData,
    name: `${childData.firstName} ${childData.lastName}`,
  })),
);
const canGoBackToModeSelection = computed(() => modesData.value?.showModeSelection === true);
const canGoBackToSlugInput = computed(
  () => hideInstitutions.value && !routeInstitutionSlug.value && manualSlug.value !== undefined,
);

// Locked institution for worker registration (when accessed via dedicated link or slug input)
const lockedInstitution = computed(() => {
  if (effectiveSlug.value && modesData.value?.institutionId && modesData.value?.institutionName) {
    return {
      id: modesData.value.institutionId,
      name: modesData.value.institutionName,
    };
  }
  return undefined;
});

// Only forward a slug that /register/modes actually resolved - the dedicated-link routes reject an unresolvable one outright.
const resolvedInstitutionSlug = computed(() =>
  lockedInstitution.value ? effectiveSlug.value : undefined,
);

// Redirect if self-registration disabled - only once settings actually loaded, not on fetch error
watchEffect(() => {
  if (
    !isLoadingPublicSettings.value &&
    !isPublicSettingsError.value &&
    publicSettings.value &&
    !publicSettings.value.parents?.allowSelfRegistration
  ) {
    router.push({ name: "auth.login" });
  }
});

// Load additional data when form is ready
watch(
  currentStep,
  async (step) => {
    if (step === "form") {
      const termsOfUse = await store.dispatch("auth/getCateringTermsOfUse");
      cateringTermsOfUse.value =
        // eslint-disable-next-line no-prototype-builtins
        termsOfUse.hasOwnProperty("cateringTermsOfUse") ? termsOfUse.cateringTermsOfUse : undefined;
    }
  },
  { immediate: true },
);

// slugInputRef may not be mounted yet when this fires, so fall back to a toast.
watch(slugFailedToResolve, (failed) => {
  if (!failed) {
    return;
  }

  if (slugInputRef.value) {
    slugInputRef.value.setError();
  } else {
    store.commit("notifications/showNotification", {
      type: "error",
      text: t("Institution with this number was not found"),
    });
  }

  manualSlug.value = undefined;
});

function onModeSelected(mode: RegistrationMode) {
  selectedMode.value = mode;
}

function goBackToModeSelection() {
  selectedMode.value = null;
}

function goBackToSlugInput() {
  manualSlug.value = undefined;
  selectedMode.value = null;
}

async function onSlugSubmit(slug: string) {
  manualSlug.value = slug;
  // refetchModes will be triggered automatically by the computed effectiveSlug change
}

async function getWorkerOptions() {
  const result = await store.dispatch(
    "auth/getInstitutionWorkerRegisterOptions",
    resolvedInstitutionSlug.value,
  );
  allergensOptions.value = result.allergensOptions;
  classroomsOptions.value = result.classroomsOptions;
  institutionWorkersGroupsOptions.value = result.institutionWorkersGroupsOptions;
}

watch(
  registerAs,
  (value) => {
    if (value === "institution_worker") {
      getWorkerOptions();
    }
  },
  { immediate: true },
);

// Children management methods
function onCreateChildButtonClick() {
  selectedChildDataIndex.value = null;
  selectedChildData.value = null;
  createChildModalVisible.value = true;
}

function onEditChildDataButtonClick(childData: ChildData, index: number) {
  selectedChildData.value = childData;
  selectedChildDataIndex.value = index;
  createChildModalVisible.value = true;
}

function onDeleteChildDataButtonClick(index: number) {
  childrenData.value.splice(index, 1);
}

function onChildDataCreated(childData: ChildData) {
  createChildModalVisible.value = false;
  childrenData.value.push(childData);
  selectedChildDataIndex.value = null;
  selectedChildData.value = null;
}

function onChildDataEdited(childData: ChildData) {
  createChildModalVisible.value = false;
  if (selectedChildDataIndex.value !== null) {
    childrenData.value[selectedChildDataIndex.value] = JSON.parse(JSON.stringify(childData));
  }
  selectedChildDataIndex.value = null;
  selectedChildData.value = null;
}

function hideCreateChildModal() {
  createChildModalVisible.value = false;
  selectedChildDataIndex.value = null;
  selectedChildData.value = null;
}

async function dispatchRegistration(
  registerData: Record<string, unknown>,
  userType: string,
  existingAccountPassword?: string,
) {
  const result = await store.dispatch("auth/registerUser", {
    registerData,
    userType,
    existingAccountPassword,
    institutionSlug: resolvedInstitutionSlug.value,
  });

  if (result) {
    // linking to an existing account reuses its password - no reset e-mail is sent for that case
    const linkedToExistingAccount = (result as { linkedToExistingAccount?: boolean })
      .linkedToExistingAccount;

    store.commit("notifications/showNotification", {
      type: "success",
      text: linkedToExistingAccount
        ? t(
            "This catering has been added to your existing Obiado account. Log in with your existing password.",
          )
        : t(
            "Your user account has been successfully registered. A password reset link has been sent to the e-mail address '{emailAddress}'.",
            { emailAddress: registerData.emailAddress as string },
          ),
    });
    hideChildrenTable.value = true;
    registrationCompleted.value = true;
  }

  return result;
}

async function onSubmit(registerData: Record<string, unknown>, userType: string) {
  if (userType === "parent") {
    registerData.children = childrenData.value;
  }

  try {
    return await dispatchRegistration(registerData, userType);
  } catch (error) {
    const errorKey = (error as { response?: { data?: { key?: string } } })?.response?.data?.key;

    if (errorKey !== EMAIL_ALREADY_REGISTERED_ELSEWHERE_ERROR_KEY) {
      await showApiErrorNotification(error);

      throw error;
    }

    // account already exists elsewhere - park this submission and ask for its password instead
    // of failing the registration outright or silently attaching the new tenant to it
    const registerDataSnapshot = JSON.parse(JSON.stringify(registerData));

    existingAccountEmail.value = registerData.emailAddress as string;
    existingAccountModalError.value = null;
    existingAccountModalVisible.value = true;

    return new Promise((resolve, reject) => {
      pendingRegistration = { registerData: registerDataSnapshot, userType, resolve, reject };
    });
  }
}

async function onExistingAccountPasswordSubmit(password: string) {
  if (!pendingRegistration) {
    return;
  }

  const { registerData, userType, resolve, reject } = pendingRegistration;

  existingAccountModalError.value = null;
  existingAccountModalSubmitting.value = true;

  try {
    const result = await dispatchRegistration(registerData, userType, password);

    existingAccountModalVisible.value = false;
    pendingRegistration = null;
    resolve(result);
  } catch (error) {
    const errorKey = (error as { response?: { data?: { key?: string } } })?.response?.data?.key;

    if (errorKey === EMAIL_ALREADY_REGISTERED_ELSEWHERE_ERROR_KEY) {
      // we sent a non-empty password and still got rejected - it must be wrong, keep the
      // modal open so the user can retry rather than losing the whole registration attempt
      existingAccountModalError.value = t(
        "The password you entered is incorrect. Please try again.",
      );
    } else if (errorKey === EXISTING_ACCOUNT_LINK_ATTEMPTS_RATE_LIMITED_ERROR_KEY) {
      // same reasoning - keep the modal open
      existingAccountModalError.value = t("Too many attempts. Please try again later.");
    } else {
      await showApiErrorNotification(error);
      existingAccountModalVisible.value = false;
      pendingRegistration = null;
      reject(error);
    }
  } finally {
    existingAccountModalSubmitting.value = false;
  }
}

function onExistingAccountModalHide() {
  existingAccountModalVisible.value = false;
  existingAccountEmail.value = null;
  existingAccountModalError.value = null;

  if (pendingRegistration) {
    pendingRegistration.reject(new Error("Registration cancelled by user."));
    pendingRegistration = null;
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

.register-card {
  border: 1px solid #d7d7d7 !important;
}

.btn-outline-primary {
  border-color: #6c757d;
  color: #6c757d;

  &:hover {
    border-color: $primary;
    background-color: $primary;
    color: white;
  }
}

.registration-type-badge {
  background-color: $primary;
  border-radius: 15px;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: white;
}
</style>
